import React, { useEffect, useState } from 'react';
import { Car, Save, Search } from 'lucide-react';
import { adminService } from '../services/AdminService';

// Per-org licence plate → VIN lookup (backend: bl/plate_lookup). Off unless
// saved enabled. The lookup runs on an MCP server (tool check_plate); its
// bearer token goes to Secret Manager on Save and is never sent back, so
// blank = keep the stored token.
type Notify = (title: string, msg: string, type: 'success' | 'error' | 'warning' | 'info') => void;

const PlateLookupPanel: React.FC<{ orgId: number; showNotification: Notify }> = ({ orgId, showNotification }) => {
  const [countries, setCountries] = useState<string[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [country, setCountry] = useState('');
  const [mcpUrl, setMcpUrl] = useState('');
  const [token, setToken] = useState('');
  const [tokenSet, setTokenSet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plate, setPlate] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);

  const load = () => adminService.getOrgPlateLookup(orgId).then(res => {
    const d = res.data;
    setCountries(d.countries);
    setEnabled(!!d.config?.enabled);
    setCountry(d.config?.country || d.countries[0] || '');
    setMcpUrl(d.config?.mcp_url || '');
    setTokenSet(!!d.token_set);
    setToken('');
  }).catch(() => showNotification('Error', 'Failed to load plate lookup settings', 'error'));

  useEffect(() => { load(); }, [orgId]);

  const save = async () => {
    setSaving(true);
    try {
      await adminService.updateOrgPlateLookup(orgId, {
        enabled, country, mcp_url: mcpUrl.trim(), token: token.trim() || undefined,
      });
      showNotification('Success', `Plate lookup ${enabled ? 'enabled' : 'disabled'} for this org`, 'success');
      load();
    } catch (e: any) {
      showNotification('Error', e?.response?.data?.detail || 'Failed to save plate lookup', 'error');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult((await adminService.testOrgPlateLookup(orgId, plate)).data);
    } catch (e: any) {
      setTestResult({ status: 'error', detail: e?.response?.data?.detail || 'Request failed' });
    } finally {
      setTesting(false);
    }
  };

  const field = 'border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400';
  const label = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="mt-3 pt-3 border-t border-gray-200">
      <h3 className="text-xs font-medium text-gray-700 flex items-center gap-2">
        <Car className="w-4 h-4 text-purple-500" />
        Licence plate → VIN lookup
      </h3>
      <p className="text-xs text-gray-500 mt-0.5 mb-3">
        Off by default. When on, a plate in the chat (typed or read from a photo) is sent to the MCP server's
        <code className="mx-1">check_plate</code> tool and the VIN it returns is used. Has its own Save; save before testing.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          Enabled
        </label>
        {enabled && (
          <>
            <div>
              <label className={label}>Country</label>
              <select className={field} value={country} onChange={e => setCountry(e.target.value)}>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[240px]">
              <label className={label}>MCP server URL</label>
              <input className={`${field} w-full`} autoComplete="off" placeholder="https://host/mcp"
                value={mcpUrl} onChange={e => setMcpUrl(e.target.value)} />
            </div>
            <div>
              <label className={label}>Bearer token</label>
              <input className={field} type="password" autoComplete="new-password"
                placeholder={tokenSet ? '•••••• (saved — blank keeps it)' : 'optional'}
                value={token} onChange={e => setToken(e.target.value)} />
            </div>
          </>
        )}
        <button onClick={save} disabled={saving || !country || (enabled && !mcpUrl.trim())}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50">
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {enabled && (
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <input className={field} placeholder="Test plate, e.g. 20-OE-90" value={plate} onChange={e => setPlate(e.target.value)} />
          <button onClick={test} disabled={testing || !plate.trim()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-purple-300 text-purple-700 rounded-md hover:bg-purple-50 disabled:opacity-50">
            <Search className="w-4 h-4" />
            {testing ? 'Looking up…' : 'Test plate'}
          </button>
          {testResult && (
            <span className={`text-sm ${testResult.status === 'found' ? 'text-green-700' : 'text-red-600'}`}>
              {testResult.status === 'found' && <>VIN <strong>{testResult.vin}</strong>{testResult.vehicle ? ` · ${testResult.vehicle}` : ''}</>}
              {testResult.status === 'not_found' && 'Server answered · plate not found'}
              {testResult.status === 'error' && `Failed: ${testResult.detail}`}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default PlateLookupPanel;
